import { useState } from "react";
import { LockOpen, Loader2, FileCheck2, Check, ArrowRight, ArrowLeft } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { api, errMsg } from "../lib/api";
import { normalizePhoneCz } from "../lib/bfu";
import { EmailHint, ResendCode } from "./BfuBits";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const STEPS = [
  { id: "billing", label: "Vaše údaje" },
  { id: "sms", label: "Ověření" },
  { id: "invoice", label: "Faktura" },
];

const StepBar = ({ current }) => {
  const idx = STEPS.findIndex((s) => s.id === current);
  return (
    <div className="mb-2 flex items-center justify-center" data-testid="purchase-step-bar">
      {STEPS.map((s, i) => (
        <div key={s.id} className="flex items-center">
          <div className="flex flex-col items-center gap-1">
            <div className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold transition-colors
              ${i < idx ? "bg-emerald-600 text-white" : i === idx ? "bg-emerald-600 text-white ring-4 ring-emerald-100" : "bg-slate-100 text-slate-400"}`}>
              {i < idx ? <Check className="h-4 w-4" /> : i + 1}
            </div>
            <span className={`text-[11px] font-semibold ${i === idx ? "text-slate-900" : "text-slate-400"}`}>{s.label}</span>
          </div>
          {i < STEPS.length - 1 && <div className={`mx-2 mb-4 h-0.5 w-10 rounded ${i < idx ? "bg-emerald-500" : "bg-slate-200"}`} />}
        </div>
      ))}
    </div>
  );
};

export const PurchaseDialog = ({ audit, priceCzk, onUnlocked, trigger }) => {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("billing");
  const [busy, setBusy] = useState(false);
  const [devCode, setDevCode] = useState(null);
  const [code, setCode] = useState("");
  const [invoice, setInvoice] = useState(null);
  const { user } = useAuth();
  const [form, setForm] = useState({ name: user?.name || "", street: "", city: "", zip: "", ico: "", dic: "",
    email: user?.email || "", phone: user?.phone || "" });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const sendSms = async () => {
    const norm = normalizePhoneCz(form.phone);
    if (!norm) {
      toast.error("Zkontrolujte prosím telefon — stačí ho napsat jako 777 123 456.");
      return null;
    }
    const payload = { ...form, phone: norm };
    setForm(payload);
    const { data } = await api.post(`/audits/${audit.id}/purchase/start`, payload);
    return data;
  };

  const start = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const data = await sendSms();
      if (!data) return;
      if (data.status === "invoice_issued") {
        setInvoice(data.invoice);
        setStep("invoice");
        toast.success(`Telefon už máte ověřený — faktura ${data.invoice.number} byla vystavena rovnou`);
      } else {
        setDevCode(data.dev_code || null);
        setStep("sms");
        toast.success("Ověřovací kód byl odeslán na váš telefon");
      }
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/audits/${audit.id}/purchase/verify`, { code });
      setInvoice(data.invoice);
      setStep("invoice");
      toast.success(`Faktura ${data.invoice.number} byla vystavena`);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const checkPayment = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/audits/${audit.id}/purchase/check-payment`);
      if (data.paid) {
        toast.success("Platba přijata — plný audit je odemčený!");
        setOpen(false);
        onUnlocked();
      } else {
        toast.info("Platba zatím nebyla připsána. Zkuste to prosím později.");
      }
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <span onClick={() => setOpen(true)}>{trigger}</span>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="purchase-dialog"
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}>
          <StepBar current={step} />

          {step === "billing" && (
            <>
              <DialogHeader>
                <DialogTitle className="text-xl">Odemknout plný přístup — {priceCzk.toLocaleString("cs-CZ")} Kč</DialogTitle>
                <DialogDescription className="text-sm">
                  Zkontrolujte prosím údaje pro fakturu. Nic dalšího vyplňovat nemusíte.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={start} className="grid grid-cols-2 gap-3">
                <div className="col-span-2 space-y-1">
                  <Label className="text-sm font-semibold">Název školy / organizace *</Label>
                  <Input required autoFocus value={form.name} onChange={set("name")} className="h-12 text-base" data-testid="billing-name-input" placeholder="Mateřská škola Sluníčko" />
                </div>
                <div className="col-span-2 space-y-1">
                  <Label className="text-sm font-semibold">Ulice a č. p. *</Label>
                  <Input required value={form.street} onChange={set("street")} className="h-12 text-base" data-testid="billing-street-input" placeholder="Květinová 123" />
                </div>
                <div className="space-y-1">
                  <Label className="text-sm font-semibold">Město *</Label>
                  <Input required value={form.city} onChange={set("city")} className="h-12 text-base" data-testid="billing-city-input" placeholder="Brandýs nad Labem" />
                </div>
                <div className="space-y-1">
                  <Label className="text-sm font-semibold">PSČ *</Label>
                  <Input required value={form.zip} onChange={set("zip")} className="h-12 text-base" data-testid="billing-zip-input" placeholder="250 01" />
                </div>
                <div className="space-y-1">
                  <Label className="text-sm font-semibold">IČO</Label>
                  <Input value={form.ico} onChange={set("ico")} className="h-12 text-base" data-testid="billing-ico-input" placeholder="12345678" />
                </div>
                <div className="space-y-1">
                  <Label className="text-sm font-semibold">DIČ <span className="font-normal text-slate-400">(jen pokud máte)</span></Label>
                  <Input value={form.dic} onChange={set("dic")} className="h-12 text-base" data-testid="billing-dic-input" placeholder="CZ12345678" />
                </div>
                <div className="space-y-1">
                  <Label className="text-sm font-semibold">E-mail *</Label>
                  <Input required type="email" value={form.email} onChange={set("email")} className="h-12 text-base" data-testid="billing-email-input" placeholder="reditelka@skolka.cz" />
                  <EmailHint email={form.email} onFix={(v) => setForm({ ...form, email: v })} />
                </div>
                <div className="space-y-1">
                  <Label className="text-sm font-semibold">Telefon *</Label>
                  <Input required type="tel" value={form.phone} onChange={set("phone")}
                    onBlur={() => { const n = normalizePhoneCz(form.phone); if (n) setForm({ ...form, phone: n }); }}
                    className="h-12 text-base" data-testid="billing-phone-input" placeholder="777 123 456" />
                </div>
                <Button type="submit" disabled={busy} className="col-span-2 h-14 bg-emerald-600 text-base font-bold hover:bg-emerald-700" data-testid="purchase-start-button">
                  {busy ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : null}
                  {busy ? "Odesílám…" : <>Pokračovat <ArrowRight className="ml-2 h-5 w-5" /></>}
                </Button>
                <button type="button" onClick={() => setOpen(false)} data-testid="purchase-close-button"
                  className="col-span-2 py-1 text-center text-sm text-slate-500 underline hover:text-slate-700">
                  Zavřít — koupím později
                </button>
              </form>
            </>
          )}

          {step === "sms" && (
            <>
              <DialogHeader>
                <DialogTitle className="text-xl">Zadejte kód z SMS</DialogTitle>
                <DialogDescription className="text-sm">
                  Poslali jsme vám SMS s 6místným kódem na číslo <b>{form.phone}</b>. Opište ho sem.
                </DialogDescription>
              </DialogHeader>
              {devCode && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="dev-sms-code">
                  DEV režim (SMS brána zatím není připojena) — váš kód: <b className="font-mono text-sm">{devCode}</b>
                </div>
              )}
              <form onSubmit={verify} className="space-y-3">
                <Input required maxLength={6} inputMode="numeric" autoFocus value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  className="h-16 text-center text-3xl tracking-[0.4em] font-mono" placeholder="······" data-testid="sms-code-input" />
                <Button type="submit" disabled={busy || code.length < 6} className="h-14 w-full bg-emerald-600 text-base font-bold hover:bg-emerald-700" data-testid="purchase-verify-button">
                  {busy ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : null} {busy ? "Ověřuji…" : "Ověřit a vystavit fakturu"}
                </Button>
                <ResendCode testId="purchase-resend-code" onResend={async () => {
                  try {
                    const data = await sendSms();
                    if (data?.status === "invoice_issued") { setInvoice(data.invoice); setStep("invoice"); }
                    else if (data) { setDevCode(data.dev_code || null); toast.success("Poslali jsme vám nový kód"); }
                  } catch (err) { toast.error(errMsg(err)); }
                }} />
                <button type="button" onClick={() => setStep("billing")} data-testid="purchase-back-button"
                  className="flex w-full items-center justify-center gap-1 py-1 text-center text-sm text-slate-500 underline hover:text-slate-700">
                  <ArrowLeft className="h-3.5 w-3.5" /> Zpět na údaje
                </button>
              </form>
            </>
          )}

          {step === "invoice" && invoice && (
            <>
              <DialogHeader>
                <DialogTitle className="text-xl">Hotovo — faktura je vystavena</DialogTitle>
                <DialogDescription className="text-sm">
                  Fakturu jsme vám poslali i na e-mail. Jakmile platba dorazí, plný audit se odemkne automaticky.
                </DialogDescription>
              </DialogHeader>
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-5 text-sm" data-testid="invoice-info-box">
                <div className="flex items-center gap-2 text-base font-semibold text-slate-900">
                  <FileCheck2 className="h-5 w-5 text-emerald-600" /> Faktura č. {invoice.number}
                </div>
                <p className="mt-1 text-base text-slate-600">Částka: <b>{invoice.amount.toLocaleString("cs-CZ")} Kč</b></p>
                {invoice.public_url && (
                  <a href={invoice.public_url} target="_blank" rel="noreferrer" className="mt-2 block text-base text-emerald-700 underline" data-testid="invoice-public-link">
                    Zobrazit fakturu online
                  </a>
                )}
                {invoice.pdf_url && (
                  <a href={invoice.pdf_url} target="_blank" rel="noreferrer" className="mt-1 block text-base text-emerald-700 underline" data-testid="invoice-pdf-link">
                    Stáhnout PDF faktury
                  </a>
                )}
                {invoice.mock && (
                  <p className="mt-2 text-xs text-amber-700">DEV režim: fakturační služba vyfakturuj.cz zatím není připojena — faktura je testovací.</p>
                )}
              </div>
              <Button onClick={checkPayment} disabled={busy} className="h-14 w-full bg-emerald-600 text-base font-bold hover:bg-emerald-700" data-testid="check-payment-button">
                {busy ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <LockOpen className="mr-2 h-5 w-5" />}
                Už jsem zaplatil/a — odemknout
              </Button>
              <button type="button" onClick={() => setOpen(false)} data-testid="purchase-pay-later-button"
                className="py-1 text-center text-sm text-slate-500 underline hover:text-slate-700">
                Zavřít — zaplatím později (odkaz máte v e-mailu)
              </button>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
